"use client";

import type React from "react";
import { useEffect } from "react";
import { useReloadAds } from "../../components/Ad/hooks";
import { useConsent } from "../../components/Consent/hooks";
import { getWindow } from "../../helpers/getWindow";
import { useIsPageChange } from "../../hooks/useIsPageChange";
import { usePageParams } from "../../hooks/usePageParams";

type Props = {
    children: React.ReactNode;
};

const ProvidersInner: React.FC<Props> = ({ children }) => {
    const { pageType } = usePageParams();
    const consent = useConsent();
    const reloadAds = useReloadAds();
    const isPageChange = useIsPageChange();
    const window = getWindow();

    useEffect(() => {
        let adsReloaded = false;

        if (isPageChange && reloadAds !== undefined) {
            reloadAds();
            adsReloaded = true;
        }

        const handlePageShow = (event: PageTransitionEvent): void => {
            if (adsReloaded || reloadAds === undefined) {
                return;
            }

            // Check for bf_cache using event.persisted (Safari, Firefox)
            if (event.persisted) {
                reloadAds();
                return;
            }

            // Check for bf_cache using PerformanceNavigationTiming (Chrome and other browsers)
            const entries = performance.getEntriesByType("navigation");
            if (entries.length > 0 && entries[0]?.type === "back_forward") {
                reloadAds();
            }
        };

        window?.addEventListener("pageshow", handlePageShow);

        return (): void => {
            window?.removeEventListener("pageshow", handlePageShow);
        };
    }, [window, reloadAds, isPageChange]);

    useEffect(() => {
        if (!consent) {
            return;
        }

        // This is used to check if user landed on Twig Forecast first,
        // the value will be "" if they do not start on Twig Forecast,
        // hence it will always be empty here
        if (window?.sessionStorage.getItem("wr6032") === null) {
            window?.sessionStorage.setItem("wr6032", "");
        }

        // This is used to check if user landed on React Forecast first,
        // the value will be "" if they do not start on React Forecast
        // Only set the value to "true" if it's a Forecast page
        if (window?.sessionStorage.getItem("wr6032-r") === null) {
            window?.sessionStorage.setItem(
                "wr6032-r",
                pageType?.isAnyForecastPage ? "true" : ""
            );
        }
    }, [consent, pageType, window]);

    return children;
};

export default ProvidersInner;
